import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  viewChild,
} from '@angular/core';
import { UI_TEXT } from '../i18n';
import { ScFieldHover } from './field-hover.directive';

/**
 * Fecha del kit (reemplaza a dx-date-box): el campo de fecha del navegador (decisión D6), con el
 * valor como texto `yyyy-MM-dd`. Se ve como el de DevExtreme: el botón del calendario a la derecha y,
 * si `clearable` y hay fecha, la × para limpiar. Como en DevExtreme, una fecha anterior a `min`
 * queda escrita y el campo se marca inválido; una fecha a medio escribir no cambia el valor.
 */
@Component({
  selector: 'sc-date-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ScFieldHover],
  host: {
    class: 'sck-field sck-date',
    '[class.sck-field--disabled]': 'disabled()',
    '[class.sck-field--invalid]': 'invalid()',
  },
  template: `
    <div class="sck-field__control">
      <input
        #field
        class="sck-field__input"
        type="date"
        [value]="value() ?? ''"
        [attr.min]="min()"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-invalid]="invalid() ? 'true' : null"
        (change)="onChange()"
        (blur)="restore()"
      />
    </div>
    @if (clearable() && value()) {
      <button
        type="button"
        class="sck-field__clear"
        tabindex="-1"
        [attr.aria-label]="text.clear"
        (mousedown)="$event.preventDefault()"
        (click)="value.set(null)"
      >
        <span class="material-icons sck-icon" aria-hidden="true" data-icon="cancel"></span>
      </button>
    }
    <button
      type="button"
      class="sck-field__button"
      tabindex="-1"
      [attr.aria-label]="text.openCalendar"
      (mousedown)="$event.preventDefault()"
      (click)="openPicker()"
    >
      <span class="sck-field__button-icon">
        <span class="material-icons sck-icon" aria-hidden="true" data-icon="event"></span>
      </span>
    </button>
  `,
})
export class ScDateInputComponent {
  /** `yyyy-MM-dd` o null. */
  readonly value = model<string | null>(null);
  readonly min = input<string | null>(null);
  readonly clearable = input(false);
  readonly disabled = input(false);
  readonly ariaLabel = input<string | null>(null);

  protected readonly text = UI_TEXT;
  protected readonly invalid = computed(() => {
    const value = this.value();
    const min = this.min();
    return Boolean(value && min && value < min);
  });
  private readonly field = viewChild.required<ElementRef<HTMLInputElement>>('field');

  protected onChange(): void {
    const el = this.field().nativeElement;
    // A medio escribir (un día sin mes, por ejemplo) el navegador entrega '' con badInput: el
    // valor no cambia, y al salir del campo vuelve a verse el anterior.
    if (el.validity.badInput) return;
    const next = el.value || null;
    if (next !== this.value()) this.value.set(next);
  }

  /**
   * Al salir del campo. Vacío del todo (los tres segmentos borrados con el teclado; el navegador no
   * avisa ese último paso con `change`) es «sin fecha»; a medio escribir, vuelve la anterior.
   */
  protected restore(): void {
    const el = this.field().nativeElement;
    if (!el.validity.badInput && el.value === '') {
      if (this.value() !== null) this.value.set(null);
      return;
    }
    if (el.validity.badInput || el.value !== (this.value() ?? '')) el.value = this.value() ?? '';
  }

  protected openPicker(): void {
    const el = this.field().nativeElement;
    el.focus();
    try {
      el.showPicker();
    } catch {
      // Sin gesto del usuario (o en un navegador sin showPicker) basta con el foco.
    }
  }
}
